import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject, of } from 'rxjs';
import { filter, shareReplay, startWith, switchMap, take } from 'rxjs/operators';
import { ServerService } from 'shared/core/services/server.service';
import { UserService } from 'shared/core/services/user/user.service';

@Injectable({ providedIn: 'root' })
export class UserImageService {
    static readonly DEFAULT_IMAGE = 'assets/app/media/img/users/user-128.png';

    private refresh$ = new BehaviorSubject<void>(undefined);
    private images = new Map<string, Observable<string>>();

    constructor(
        private serverService: ServerService,
        private userService: UserService
    ) {}

    /** Data URL of the user's image, or the default if none/missing. */
    get(name?: string | null): Observable<string> {
        if (!name) return of(UserImageService.DEFAULT_IMAGE);

        let img$ = this.images.get(name);
        if (!img$) {
            img$ = this.refresh$.pipe(
                switchMap(() =>
                    this.userService.on_ready.pipe(
                        filter(ready => ready),
                        take(1)
                    )
                ),
                switchMap(() => this.fetch(name)),
                startWith(UserImageService.DEFAULT_IMAGE),
                shareReplay({ bufferSize: 1, refCount: false })
            );
            this.images.set(name, img$);
        }
        return img$;
    }

    /** Call after a successful changeImageProfile so displayed avatars refetch. */
    refresh(): void {
        this.refresh$.next();
    }

    private fetch(name: string): Observable<string> {
        return new Observable<string>(subscriber => {
            const cancel$ = new Subject<void>();

            this.serverService.post(
                'services/user-image/getUserImage',
                { name },
                data => {
                    subscriber.next(data?.image || UserImageService.DEFAULT_IMAGE);
                    subscriber.complete();
                },
                _error => {
                    subscriber.next(UserImageService.DEFAULT_IMAGE);
                    subscriber.complete();
                },
                cancel$,
                true // avoidLoading: no global spinner for an avatar
            );

            return () => cancel$.next();
        });
    }
}
